import React, { useState, useEffect, useRef } from 'react';
import { API_BASE } from '../../config';
import { useTranslation } from '../../i18n/LanguageContext';
import { Target, Plus, TrendingUp, Calendar, CheckCircle, Clock, XCircle, Loader2 } from 'lucide-react';
import './Performance.css';

function ObjectivesList({ user, onNavigate, hideHeader = false, t: propT }) {
  const { t: translation } = useTranslation();
  const t = propT || translation;
  const [objectives, setObjectives] = useState([]);
  const [showNewModal, setShowNewModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [filters, setFilters] = useState({
    period_type: '',
    period_year: new Date().getFullYear(),
    status: ''
  });
  
  const [newObjective, setNewObjective] = useState({
    employee_id: '',
    title: '',
    description: '',
    category: 'general',
    period_type: 'quarterly',
    period_year: new Date().getFullYear(),
    period_quarter: Math.floor(new Date().getMonth() / 3) + 1,
    target_value: '',
    unit: '%',
    start_date: '',
    end_date: ''
  });

  const [updatingObjectiveId, setUpdatingObjectiveId] = useState(null);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [progressInputs, setProgressInputs] = useState({});

  const canCreateObjective = ['admin', 'manager', 'employee'].includes(user?.role);

  useEffect(() => {
    loadObjectives();
    if (canCreateObjective) {
      loadEmployees();
    }
  }, [filters, user?.role]);

  // Charger les employés quand le modal s'ouvre (5.2 all roles can create → need employee list to assign)
  useEffect(() => {
    if (showNewModal && canCreateObjective) {
      loadEmployees();
    }
  }, [showNewModal, user?.role]);

  const loadEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/employees?all=true&active=all&allRoles=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // ✅ CORRECTION : Gérer la structure paginée (data.employees ou data.data)
        if (data.success) {
          const employeesList = data.employees || data.data || [];
          if (Array.isArray(employeesList)) {
            // Mapper les employés pour s'assurer d'avoir first_name et last_name
            const mappedEmployees = employeesList.map(emp => ({
            id: emp.id,
            first_name: emp.firstName || emp.first_name || '',
            last_name: emp.lastName || emp.last_name || '',
            fullName: emp.fullName || `${emp.firstName || emp.first_name || ''} ${emp.lastName || emp.last_name || ''}`.trim()
            }));
            setEmployees(mappedEmployees);
          } else {
            setEmployees([]);
          }
        } else {
          setEmployees([]);
        }
      }
    } catch (error) {
      console.error('Erreur chargement employés:', error);
      setEmployees([]); // S'assurer que employees reste un tableau en cas d'erreur
    }
  };

  const loadObjectives = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (filters.period_type) params.append('period_type', filters.period_type);
      if (filters.period_year) params.append('period_year', filters.period_year);
      if (filters.status) params.append('status', filters.status);
      
      const res = await fetch(`${API_BASE}/api/performance/objectives?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error(t('performance.error_loading'));
      
      const data = await res.json();
      setObjectives(data.objectives || []);
    } catch (error) {
      console.error('Erreur chargement objectifs:', error);
      alert(t('performance.error_loading'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateObjective = async () => {
    try {
      if (!newObjective.employee_id || !newObjective.title || !newObjective.start_date || !newObjective.end_date) {
        alert(t('performance.error_fill_all_required'));
        return;
      }
      setCreateLoading(true);
      // Préparer les données pour l'API
      const objectiveData = {
        employee_id: newObjective.employee_id,
        title: newObjective.title,
        description: newObjective.description || null,
        category: newObjective.category || 'general',
        period_type: newObjective.period_type,
        period_year: parseInt(newObjective.period_year) || new Date().getFullYear(),
        period_quarter: newObjective.period_type === 'quarterly' ? parseInt(newObjective.period_quarter) || 1 : null,
        period_month: newObjective.period_type === 'monthly' ? parseInt(newObjective.period_month) || new Date().getMonth() + 1 : null,
        target_value: newObjective.target_value ? parseFloat(newObjective.target_value) : null,
        unit: newObjective.unit || null,
        start_date: newObjective.start_date,
        end_date: newObjective.end_date
      };

      console.log('📤 Création objectif - Données envoyées:', objectiveData);

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/performance/objectives`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(objectiveData)
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || t('performance.error_creating'));
      }

      setShowNewModal(false);
      setNewObjective({
        employee_id: '',
        title: '',
        description: '',
        category: 'general',
        period_type: 'quarterly',
        period_year: new Date().getFullYear(),
        period_quarter: Math.floor(new Date().getMonth() / 3) + 1,
        period_month: new Date().getMonth() + 1,
        target_value: '',
        unit: '%',
        start_date: '',
        end_date: ''
      });
      loadObjectives();
      alert(t('performance.success_created'));
    } catch (error) {
      console.error('Erreur création objectif:', error);
      alert(error.message || t('performance.error_creating'));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUpdateProgress = async (id, currentValue) => {
    const num = parseFloat(currentValue);
    if (Number.isNaN(num) && currentValue !== '' && currentValue !== undefined) return;
    setUpdatingObjectiveId(id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/performance/objectives/${id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ current_value: Number.isNaN(num) ? null : num })
      });
      if (!res.ok) throw new Error(t('performance.error_updating'));
      setProgressInputs((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      if (selectedObjective && selectedObjective.id === id) setSelectedObjective(null);
      loadObjectives();
    } catch (error) {
      console.error('Erreur mise à jour:', error);
      alert(t('performance.error_updating'));
    } finally {
      setUpdatingObjectiveId(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} color="#10B981" />;
      case 'active':
        return <Clock size={16} color="#3B82F6" />;
      case 'cancelled':
        return <XCircle size={16} color="#EF4444" />;
      default:
        return <Clock size={16} color="#6B7280" />;
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: t('performance.status_active'),
      completed: t('performance.status_completed'),
      cancelled: t('performance.status_cancelled'),
      on_hold: t('performance.status_on_hold')
    };
    return labels[status] || status;
  };

  const getProgressColor = (progress) => {
    if (progress >= 100) return '#10B981';
    if (progress >= 75) return '#3B82F6';
    if (progress >= 50) return '#F59E0B';
    return '#EF4444';
  };

  if (loading) {
    return (
      <div className="performance-container">
        <div className="loading">{t('performance.loading')}</div>
      </div>
    );
  }

  return (
    <div className={hideHeader ? '' : 'performance-container'}>
      {!hideHeader && (
      <div className="objectives-header">
        <div>
          <h1><Target size={32} /> {t('performance.objectives_title')}</h1>
          <p>{t('performance.objectives_subtitle')}</p>
        </div>
        {canCreateObjective && (
          <button className="btn-primary" onClick={() => setShowNewModal(true)}>
            <Plus size={20} /> {t('performance.new_objective')}
          </button>
        )}
      </div>
      )}
      
      {/* Bouton Nouveau (si header masqué) */}
      {hideHeader && user && canCreateObjective && (
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <button 
            className="btn-primary" 
            onClick={() => setShowNewModal(true)} 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: 600,
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              backgroundColor: '#3b82f6',
              color: '#ffffff'
            }}
          >
            <Plus size={20} /> {t('performance.new_objective')}
          </button>
        </div>
      )}
      
      {/* Filtres */}
      <div className="filters-section">
        <select
          value={filters.period_type}
          onChange={(e) => setFilters({ ...filters, period_type: e.target.value })}
          style={{
            color: '#fff'
          }}
        >
          <option value="" style={{ background: '#1e293b', color: '#fff' }}>{t('performance.all_types')}</option>
          <option value="annual" style={{ background: '#1e293b', color: '#fff' }}>{t('performance.annual')}</option>
          <option value="quarterly" style={{ background: '#1e293b', color: '#fff' }}>{t('performance.quarterly')}</option>
          <option value="monthly" style={{ background: '#1e293b', color: '#fff' }}>{t('performance.monthly')}</option>
        </select>
        
        <input
          type="number"
          placeholder={t('performance.year')}
          value={filters.period_year}
          onChange={(e) => setFilters({ ...filters, period_year: parseInt(e.target.value) || '' })}
          style={{ width: '100px' }}
        />
        
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          style={{
            color: '#fff'
          }}
        >
          <option value="" style={{ background: '#1e293b', color: '#fff' }}>{t('performance.all_statuses')}</option>
          <option value="active" style={{ background: '#1e293b', color: '#fff' }}>{t('performance.status_active')}</option>
          <option value="completed" style={{ background: '#1e293b', color: '#fff' }}>{t('performance.status_completed')}</option>
          <option value="cancelled" style={{ background: '#1e293b', color: '#fff' }}>{t('performance.status_cancelled')}</option>
        </select>
      </div>

      {/* Liste des objectifs */}
      <div className="objectives-grid">
        {objectives.length === 0 ? (
          <div className="empty-state">
            <Target size={64} color="#9CA3AF" />
            <p>{t('performance.no_objectives')}</p>
          </div>
        ) : (
          objectives.map((obj) => {
            const progressPct = Number(obj.progress_percentage ?? obj.progress_percent ?? 0) || 0;
            const isUpdating = updatingObjectiveId === obj.id;
            const canUpdateProgress = Boolean(obj.is_assignee && obj.status === 'active');
            return (
            <div
              key={obj.id}
              className="objective-card"
              onClick={canUpdateProgress ? () => {
                setSelectedObjective(obj);
                setProgressInputs((p) => ({
                  ...p,
                  [obj.id]: p[obj.id] ?? (obj.current_value != null && obj.current_value !== '' ? String(obj.current_value) : '')
                }));
              } : undefined}
              style={canUpdateProgress ? { cursor: 'pointer' } : undefined}
              role={canUpdateProgress ? 'button' : undefined}
            >
              <div className="objective-header">
                <div>
                  <h3>{obj.title}</h3>
                  <p className="objective-meta">
                    {obj.first_name} {obj.last_name}
                    {obj.department && ` • ${obj.department}`}
                  </p>
                </div>
                <div className="objective-status">
                  {getStatusIcon(obj.status)}
                  <span>{getStatusLabel(obj.status)}</span>
                </div>
              </div>

              {obj.description && (
                <p className="objective-description">{obj.description}</p>
              )}

              <div className="objective-progress">
                <div className="progress-header">
                  <span>{t('performance.progress')}</span>
                  <span style={{ color: getProgressColor(progressPct) }}>
                    {Number(progressPct).toFixed(1)}%
                  </span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${progressPct}%`,
                      backgroundColor: getProgressColor(progressPct)
                    }}
                  />
                </div>
                <div className="progress-values">
                  <span>
                    {obj.current_value || 0} {obj.unit || ''}
                  </span>
                  <span>/</span>
                  <span>
                    {obj.target_value || 0} {obj.unit || ''}
                  </span>
                </div>
              </div>

              <div className="objective-info">
                <div className="info-item">
                  <Calendar size={16} />
                  <span>
                    {obj.period_type === 'annual' && t('performance.annual')}
                    {obj.period_type === 'quarterly' && `${t('performance.quarter_T')}${obj.period_quarter} ${obj.period_year}`}
                    {obj.period_type === 'monthly' && `${obj.period_month}/${obj.period_year}`}
                  </span>
                </div>
                <div className="info-item">
                  <TrendingUp size={16} />
                  <span>{obj.category}</span>
                </div>
              </div>

              {/* Mise à jour du progrès (employé): champ + bouton Submit avec loading */}
              {canUpdateProgress && (
                <div className="objective-actions" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="number"
                    placeholder={t('performance.current_value_placeholder')}
                    value={progressInputs[obj.id] ?? ''}
                    onChange={(e) => setProgressInputs((p) => ({ ...p, [obj.id]: e.target.value }))}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255,255,255,0.2)',
                      background: 'rgba(255,255,255,0.05)',
                      color: '#fff',
                      flex: 1,
                      minWidth: 0
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const val = progressInputs[obj.id];
                      if (val === '' || val === undefined) return;
                      handleUpdateProgress(obj.id, val);
                    }}
                    disabled={isUpdating || progressInputs[obj.id] === '' || progressInputs[obj.id] === undefined}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: 'none',
                      background: isUpdating ? 'rgba(59,130,246,0.5)' : '#3b82f6',
                      color: '#fff',
                      fontWeight: 600,
                      cursor: isUpdating ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    {isUpdating ? (
                      <>
                        <Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} />
                        {t('performance.submitting') || 'Submitting…'}
                      </>
                    ) : (
                      t('common.save') || 'Submit'
                    )}
                  </button>
                </div>
              )}
            </div>
          );
          })
        )}
      </div>

      {/* Modal: clic sur la carte (employé) → formulaire pour mettre à jour le progrès */}
      {selectedObjective && selectedObjective.is_assignee && selectedObjective.status === 'active' && (
        <div className="modal-overlay" onClick={() => setSelectedObjective(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <h2>{selectedObjective.title}</h2>
            {selectedObjective.description && (
              <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '16px' }}>{selectedObjective.description}</p>
            )}
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label>{t('performance.target_value_label')}</label>
              <p style={{ margin: '4px 0', fontSize: '18px', fontWeight: 600 }}>
                {selectedObjective.current_value ?? 0} {selectedObjective.unit || ''} / {selectedObjective.target_value ?? 0} {selectedObjective.unit || ''}
              </p>
            </div>
            <div className="form-group">
              <label>{t('performance.current_value_placeholder')} *</label>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '8px' }}>
                <input
                  type="number"
                  placeholder={t('performance.current_value_placeholder')}
                  value={progressInputs[selectedObjective.id] ?? ''}
                  onChange={(e) => setProgressInputs((p) => ({ ...p, [selectedObjective.id]: e.target.value }))}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.05)',
                    color: '#fff',
                    flex: 1
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const val = progressInputs[selectedObjective.id];
                    if (val === '' || val === undefined) return;
                    handleUpdateProgress(selectedObjective.id, val);
                  }}
                  disabled={updatingObjectiveId === selectedObjective.id || !progressInputs[selectedObjective.id]}
                  style={{
                    padding: '12px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: updatingObjectiveId === selectedObjective.id ? 'rgba(59,130,246,0.5)' : '#3b82f6',
                    color: '#fff',
                    fontWeight: 600,
                    cursor: updatingObjectiveId === selectedObjective.id ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {updatingObjectiveId === selectedObjective.id ? (
                    <>
                      <Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} />
                      {t('performance.submitting') || 'Submitting…'}
                    </>
                  ) : (
                    t('common.save') || 'Submit'
                  )}
                </button>
              </div>
            </div>
            <div style={{ marginTop: '20px' }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setSelectedObjective(null)}
              >
                {t('performance.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Nouvel Objectif */}
      {showNewModal && (
        <div className="modal-overlay" onClick={() => setShowNewModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t('performance.create_objective')}</h2>
            
            <div className="form-group">
              <label>{t('performance.employee_label')}</label>
              <select
                value={newObjective.employee_id}
                onChange={(e) => setNewObjective({ ...newObjective, employee_id: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              >
                <option value="" style={{ background: '#1e293b', color: '#fff' }}>{t('performance.select_employee')}</option>
                {Array.isArray(employees) && employees.map((emp) => (
                  <option key={emp.id} value={emp.id} style={{ background: '#1e293b', color: '#fff' }}>
                    {emp.first_name} {emp.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{t('performance.title_label')}</label>
              <input
                type="text"
                value={newObjective.title}
                onChange={(e) => setNewObjective({ ...newObjective, title: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('performance.description_label')}</label>
              <textarea
                value={newObjective.description}
                onChange={(e) => setNewObjective({ ...newObjective, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('performance.period_type_label')}</label>
                <select
                  value={newObjective.period_type}
                  onChange={(e) => setNewObjective({ ...newObjective, period_type: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                >
                  <option value="annual" style={{ background: '#1e293b', color: '#fff' }}>{t('performance.annual')}</option>
                  <option value="quarterly" style={{ background: '#1e293b', color: '#fff' }}>{t('performance.quarterly')}</option>
                  <option value="monthly" style={{ background: '#1e293b', color: '#fff' }}>{t('performance.monthly')}</option>
                </select>
              </div>

              <div className="form-group">
                <label>{t('performance.year_label')} *</label>
                <input
                  type="number"
                  value={newObjective.period_year}
                  onChange={(e) => setNewObjective({ ...newObjective, period_year: parseInt(e.target.value) })}
                  required
                />
              </div>

              {newObjective.period_type === 'quarterly' && (
                <div className="form-group">
                  <label>{t('performance.quarter_label')}</label>
                  <select
                    value={newObjective.period_quarter}
                    onChange={(e) => setNewObjective({ ...newObjective, period_quarter: parseInt(e.target.value) })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '14px',
                      fontFamily: 'inherit'
                    }}
                  >
                    <option value={1} style={{ background: '#1e293b', color: '#fff' }}>T1</option>
                    <option value={2} style={{ background: '#1e293b', color: '#fff' }}>T2</option>
                    <option value={3} style={{ background: '#1e293b', color: '#fff' }}>T3</option>
                    <option value={4} style={{ background: '#1e293b', color: '#fff' }}>T4</option>
                  </select>
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('performance.target_value_label')}</label>
                <input
                  type="number"
                  step="0.01"
                  value={newObjective.target_value}
                  onChange={(e) => setNewObjective({ ...newObjective, target_value: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>{t('performance.unit_label')}</label>
                <input
                  type="text"
                  value={newObjective.unit}
                  onChange={(e) => setNewObjective({ ...newObjective, unit: e.target.value })}
                  placeholder={t('performance.unit_placeholder')}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('performance.start_date_label')}</label>
                <input
                  type="date"
                  value={newObjective.start_date}
                  onChange={(e) => setNewObjective({ ...newObjective, start_date: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('performance.end_date_label')}</label>
                <input
                  type="date"
                  value={newObjective.end_date}
                  onChange={(e) => setNewObjective({ ...newObjective, end_date: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowNewModal(false)}>
                {t('performance.cancel')}
              </button>
              <button className="btn-primary" onClick={handleCreateObjective} disabled={createLoading}>
                {createLoading ? <><Loader2 size={18} className="spin" /> {t('performance.creating') || 'Creating…'}</> : t('performance.create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ObjectivesList;

